/**
 * Enum vocabulary.
 *
 * The API sends every enum as the same `snake_case` text it stores (a value on
 * the wire, in Postgres, and in a check constraint are literally one string).
 * ASP.NET's OpenAPI output does not enumerate those values, so
 * `api-types.ts` types them as `unknown` — the one hole in the generated
 * contract.
 *
 * These unions fill it. They mirror `PersonalFinance.Core.Enums` exactly and are
 * the only hand-written types in the app; if the backend adds a value, add it
 * here and the exhaustive `switch`es in the screens will point at what to update.
 */

export type AccountType = 'depository' | 'credit' | 'loan' | 'investment' | 'other'

export type PayoffStrategy =
  | 'minimum'
  | 'percent_of_statement'
  | 'statement_balance'
  | 'fixed_amount'
  | 'current_balance'

export type AprType = 'purchase' | 'balance_transfer' | 'cash'

/**
 * The order surplus money is thrown at cards in a Phase 6 projection. Not a
 * stored policy like `PayoffStrategy` — a modeling choice made per request.
 */
export type DebtPayoffOrder = 'avalanche' | 'snowball' | 'blended'

export type PlaidItemStatus =
  | 'healthy'
  | 'login_required'
  | 'pending_expiration'
  | 'pending_disconnect'
  | 'error'
  | 'disconnected'

export type AlertType =
  | 'due_date_approaching'
  | 'statement_closed'
  | 'overdue'
  | 'planned_payment_shortfall'
  | 'interest_will_accrue'
  | 'spending_threshold'
  | 'promo_apr_expiring'
  | 'item_error'
  | 'new_accounts_available'
  | 'new_subscription_detected'
  | 'subscription_price_change'
  | 'transaction_activity'

export type AlertStatus = 'pending' | 'sent' | 'failed' | 'suppressed' | 'acknowledged'
export type AlertSeverity = 'info' | 'warning' | 'critical'
export type DeliveryStatus = 'pending' | 'sent' | 'failed' | 'dead'
export type NotificationChannel = 'discord' | 'email'

/** When a transaction watch speaks: only on a day with activity, or every day. */
export type WatchSendWhen = 'activity' | 'always'

/** Where a category came from. Precedence: manual > rule > ai > plaid. */
export type CategorySource = 'plaid' | 'rule' | 'ai' | 'manual'
/** Where a member attribution came from. */
export type AttributionSource = 'plaid_subaccount' | 'rule' | 'account_default' | 'ai' | 'manual'
/** Where an inflow classification came from. */
export type InflowSource = 'heuristic' | 'rule' | 'ai' | 'manual'

export type InflowType =
  | 'earned_income'
  | 'one_time_income'
  | 'investment_income'
  | 'gift'
  | 'internal_transfer'
  | 'family_transfer'
  | 'reimbursement'
  | 'refund'
  | 'loan_proceeds'
  | 'unknown'

export type RuleMatcherType =
  | 'merchant_contains'
  | 'merchant_regex'
  | 'name_contains'
  | 'amount_range'
  | 'account'
  | 'composite'

export type RuleOrigin = 'user' | 'ai_proposed'
export type RuleStatus = 'active' | 'pending_review' | 'rejected'

export type BillingCadence = 'weekly' | 'monthly' | 'quarterly' | 'annual' | 'custom_days'
export type SubscriptionStatus = 'active' | 'paused' | 'canceled'
export type SubscriptionLinkSource = 'manual' | 'rule' | 'ai'

export type BudgetRuleType =
  | 'income_percentage'
  | 'category_amount'
  | 'category_percentage'
  | 'account_amount'
  | 'account_cycle_amount'

/** `rolling_30d` is spelled out in the backend's override table, not derived. */
export type BudgetWindow = 'calendar_month' | 'statement_cycle' | 'rolling_30d'
export type IncomeBasis = 'gross' | 'net'

export type IncomeKind = 'salary' | 'side_hustle' | 'business' | 'investment' | 'rental' | 'other'
export type IncomePeriod = 'monthly' | 'annual'

export type AiMessageRole = 'system' | 'user' | 'assistant' | 'tool'

/**
 * Narrows an enum field that `api-types.ts` typed as `unknown`.
 *
 * ```ts
 * const type = asEnum<AccountType>(account.type)
 * ```
 *
 * This is the single sanctioned cast in the app: the value really is one of
 * these strings, the generator just could not say so.
 */
export function asEnum<T extends string>(value: unknown): T {
  return value as T
}

/** Human labels. Screens should never print a raw `snake_case` value. */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  depository: 'Cash',
  credit: 'Credit',
  loan: 'Loan',
  investment: 'Investment',
  other: 'Other',
}

export const PAYOFF_STRATEGY_LABELS: Record<PayoffStrategy, string> = {
  minimum: 'Minimum payment',
  percent_of_statement: 'Percent of statement',
  statement_balance: 'Statement balance in full',
  fixed_amount: 'Fixed amount',
  current_balance: 'Current balance in full',
}

export const APR_TYPE_LABELS: Record<AprType, string> = {
  purchase: 'Purchase',
  balance_transfer: 'Balance transfer',
  cash: 'Cash advance',
}

export const PLAID_ITEM_STATUS_LABELS: Record<PlaidItemStatus, string> = {
  healthy: 'Connected',
  login_required: 'Sign-in needed',
  pending_expiration: 'Consent expiring',
  pending_disconnect: 'Disconnect scheduled',
  error: 'Connection error',
  disconnected: 'Disconnected',
}

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  due_date_approaching: 'Due date approaching',
  statement_closed: 'Statement closed',
  overdue: 'Overdue',
  planned_payment_shortfall: 'Payment shortfall',
  interest_will_accrue: 'Interest will accrue',
  spending_threshold: 'Spending threshold',
  promo_apr_expiring: 'Promo APR expiring',
  item_error: 'Connection error',
  new_accounts_available: 'New accounts available',
  new_subscription_detected: 'New subscription detected',
  subscription_price_change: 'Subscription price change',
  transaction_activity: 'Card activity',
}

export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  failed: 'Failed',
  suppressed: 'Suppressed',
  acknowledged: 'Acknowledged',
}

export const WATCH_SEND_WHEN_LABELS: Record<WatchSendWhen, string> = {
  activity: 'Only when something happens',
  always: 'Every day, quiet or not',
}

export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
}

export const INFLOW_TYPE_LABELS: Record<InflowType, string> = {
  earned_income: 'Earned income',
  one_time_income: 'One-time income',
  investment_income: 'Investment income',
  gift: 'Gift',
  internal_transfer: 'Internal transfer',
  family_transfer: 'Family transfer',
  reimbursement: 'Reimbursement',
  refund: 'Refund',
  loan_proceeds: 'Loan proceeds',
  unknown: 'Unclassified',
}

export const BILLING_CADENCE_LABELS: Record<BillingCadence, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  custom_days: 'Custom interval',
}

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  canceled: 'Canceled',
}

export const RULE_STATUS_LABELS: Record<RuleStatus, string> = {
  active: 'Active',
  pending_review: 'Pending review',
  rejected: 'Rejected',
}

export const RULE_ORIGIN_LABELS: Record<RuleOrigin, string> = {
  user: 'Written by you',
  ai_proposed: 'Proposed by AI',
}

export const RULE_MATCHER_LABELS: Record<RuleMatcherType, string> = {
  merchant_contains: 'Merchant contains',
  merchant_regex: 'Merchant matches regex',
  name_contains: 'Name contains',
  amount_range: 'Amount between',
  account: 'Account is',
  composite: 'All of',
}

export const BUDGET_RULE_TYPE_LABELS: Record<BudgetRuleType, string> = {
  income_percentage: 'Percent of income',
  category_amount: 'Category cap',
  category_percentage: 'Category percent of income',
  account_amount: 'Account cap',
  account_cycle_amount: 'Account cap per cycle',
}

export const BUDGET_WINDOW_LABELS: Record<BudgetWindow, string> = {
  calendar_month: 'Calendar month',
  statement_cycle: 'Statement cycle',
  rolling_30d: 'Rolling 30 days',
}

export const INCOME_KIND_LABELS: Record<IncomeKind, string> = {
  salary: 'Salary',
  side_hustle: 'Side hustle',
  business: 'Business',
  investment: 'Investment',
  rental: 'Rental',
  other: 'Other',
}

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  discord: 'Discord',
  email: 'Email',
}

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  pending: 'Queued',
  sent: 'Delivered',
  failed: 'Retrying',
  dead: 'Gave up',
}

/**
 * Provenance shared across the three attribution pipelines. `ProvenanceBadge`
 * takes one of these; the AI value is the one that must always be visible.
 */
export type Provenance = 'manual' | 'rule' | 'ai' | 'plaid' | 'heuristic' | 'account_default'
